import React, { useState, useEffect, useRef } from 'react';
import { MenuRootItem, SipMenuItem } from '../../siplet-api';
import { PiletOnlyMenu } from '../PiletOnlyMenu/PiletOnlyMenu';
import { VersionInfo } from '../VersionInfo/VersionInfo';
import { ExtensionSlot, storage } from 'piral-core';
import { useTranslate } from 'piral-translate';
import { useKeycloak } from '../../keycloak-api';

import './sidebar.css';
import { SipLogo } from '../SipLogo/SipLogo';

export const Sidebar = () => {
  const translate = useTranslate();
  const { keycloak } = useKeycloak();
  const { authenticated } = useKeycloak();
  const dashboardItem: MenuRootItem = {
    route: '/',
    icon: require('../../assets/svgs/home.svg'),
    title: translate('apps'),
  };

  const manageAccount = React.useCallback(() => {
    keycloak.accountManagement();
  }, []);

  const logout = React.useCallback(() => {
    keycloak.logout({ redirectUri: window.location.origin });
  }, []);

  const [darkMode, setDarkMode] = React.useState(() => {
    const isDarkMode = storage.getItem('theme') === 'dark';
    if (isDarkMode) {
      window.document.documentElement.classList.add('dark');
    }
    return isDarkMode;
  });

  const toggleDarkMode = () => {
    const newTheme = darkMode ? 'light' : 'dark';
    storage.setItem('theme', newTheme);
    setDarkMode(!darkMode);

    if (newTheme === 'dark') {
      window.document.documentElement.classList.add('dark');
    } else {
      window.document.documentElement.classList.remove('dark');
    }
  };

  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [manuallyToggled, setManuallyToggled] = useState(false);
  const sidebarRef = useRef(null);
  const email = keycloak.idTokenParsed?.['email'];
  const name = keycloak.idTokenParsed?.['name'];
  const version = process.env.BUILD_PCKG_VERSION;

  const toggleSidebar = e => {
    e.stopPropagation();
    setManuallyToggled(true);
    setSidebarOpen(!isSidebarOpen);
  };

  const handleOutsideClick = e => {
    if (sidebarRef.current && !sidebarRef.current.contains(e.target) && isSidebarOpen && isSmallScreen) {
      setSidebarOpen(false);
      setManuallyToggled(false);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 720);
      if (window.innerWidth > 720) {
        setSidebarOpen(true);
      } else {
        if (!manuallyToggled) {
          setTimeout(() => {
            setSidebarOpen(false);
          }, 500);
        }
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    document.addEventListener('click', handleOutsideClick);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [isSidebarOpen, isSmallScreen]);

  return (
    <>
      <div ref={sidebarRef} id="application-sidebar" className={`neo-app-sidebar ${isSmallScreen ? (isSidebarOpen ? 'expanded' : 'collapsed') : 'collapsed'}`}>
        <div className="neo-nav-bar">
          <button className="neo-nav-menu-button" aria-controls="application-sidebar" aria-label="Toggle navigation" onClick={toggleSidebar}>
            <svg id="b" xmlns="http://www.w3.org/2000/svg" width="17.272mm" height="17.272mm" viewBox="0 0 48.96 48.96">
              <g id="c">
                <line id="d" className="neo-menu-icon" x1="1" y1="47.96" x2="47.96" y2="1"></line>
                <line id="e" className="neo-menu-icon" x1="1" y1="1" x2="47.96" y2="47.96"></line>
              </g>
            </svg>
          </button>
          <div onClick={toggleSidebar} className="neo-nav-bar-logo">
            <SipLogo isAuthenticated={true} />
          </div>
        </div>
        <div className="neo-lurchi"></div>
        <div className="neo-account-info">
          <div className="neo-account-details">
            <h1>{translate('account')}</h1>
            <div className="neo-account-name">
              <h3>{name}</h3>
              <p>{email}</p>
            </div>
          </div>
          <div onClick={logout} className="neo-account-button">
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
              <path d="M480-140q-70.77 0-132.61-26.77-61.85-26.77-107.85-72.77-46-46-72.77-107.85Q140-409.23 140-480q0-70.77 26.77-132.61 26.77-61.85 72.77-107.85 46-46 107.85-72.77Q409.23-820 480-820v60q-117 0-198.5 81.5T200-480q0 117 81.5 198.5T480-200v60Zm160-154.23L597.85-337l113.38-113H370v-60h340.85l-113-114L640-665.77 825.77-480 640-294.23Z" />
            </svg>

            <p>{translate('abmelden')}</p>
          </div>
          <div onClick={manageAccount} className="neo-account-button">
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
              <path d="M400-492.31q-57.75 0-98.87-41.12Q260-574.56 260-632.31q0-57.75 41.13-98.87 41.12-41.13 98.87-41.13 57.75 0 98.87 41.13Q540-690.06 540-632.31q0 57.75-41.13 98.88-41.12 41.12-98.87 41.12ZM100-187.69v-88.93q0-30.3 15.46-54.88 15.46-24.58 43.16-38.04 49.84-24.84 107.69-41.5 57.84-16.65 133.69-16.65h11.69q4.85 0 10.46 1.23-6.07 14.15-10.03 28.84-3.97 14.7-6.58 29.93H400q-69.08 0-122.31 15.88-53.23 15.89-91.54 35.81-13.61 7.31-19.88 17.08t-6.27 22.3v28.93h252q4.46 15.23 11.58 30.92 7.11 15.69 15.65 29.08H100Zm544.23 29.61-8.92-53.08q-14.31-4.23-26.93-11.07-12.61-6.85-24-17.16l-50.69 17.62-28.46-48.39L546.62-304q-4.31-15.54-4.31-30.62 0-15.07 4.31-30.61l-41-34.62 28.46-48.38 50.3 18q11-10.31 23.81-16.96 12.81-6.66 27.12-10.89l8.92-53.07h56.92l8.54 53.07q14.31 4.23 27.12 11.2 12.8 6.96 23.8 17.88l50.31-19.23 28.46 49.61-41 34.62q4.31 14.43 4.31 30.06 0 15.63-4.31 29.94l41.39 33.84-28.46 48.39-50.7-17.62q-11.38 10.31-24 17.16-12.61 6.84-26.92 11.07l-8.54 53.08h-56.92Zm28.11-100.38q31.43 0 53.77-22.38 22.35-22.38 22.35-53.81 0-31.43-22.38-53.77-22.38-22.35-53.81-22.35-31.42 0-53.77 22.38t-22.35 53.81q0 31.42 22.38 53.77t53.81 22.35ZM400-552.31q33 0 56.5-23.5t23.5-56.5q0-33-23.5-56.5t-56.5-23.5q-33 0-56.5 23.5t-23.5 56.5q0 33 23.5 56.5t56.5 23.5Zm0-80Zm12 384.62Z" />
            </svg>

            <p>{translate('account-verwalten')}</p>
          </div>
          <div onClick={toggleDarkMode} className="neo-account-button">
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
              <path d="M480.07-100q-78.84 0-148.21-29.92t-120.68-81.21q-51.31-51.29-81.25-120.63Q100-401.1 100-479.93q0-78.84 29.92-148.21t81.21-120.68q51.29-51.31 120.63-81.25Q401.1-860 479.93-860q78.84 0 148.21 29.92t120.68 81.21q51.31 51.29 81.25 120.63Q860-558.9 860-480.07q0 78.84-29.92 148.21t-81.21 120.68q-51.29 51.31-120.63 81.25Q558.9-100 480.07-100ZM510-161.85q121.31-11.53 205.65-101.42Q800-353.15 800-480t-83.96-216.35q-83.96-89.5-206.04-101.8v636.3Z" />
            </svg>

            <p>{translate('theme-wechseln')}</p>
          </div>
        </div>
        <div className="neo-sidebar-content scrollbar-hide">
          <nav className="neo-sidebar-nav">
            <ul onClick={toggleSidebar}>
              {authenticated && <SipMenuItem item={dashboardItem} />}
              <PiletOnlyMenu type="sidebar" />
            </ul>
          </nav>
          <div className="neo-sidebar-bottom">
            <div className="neo-sidebar-slot">
              <ExtensionSlot name="bottom-sidebar" />
              <VersionInfo />
            </div>
            <div className="neo-sidebar-imprint">
              <a href={translate('impressum-link')} target="_blank">
                {translate('impressum')}
              </a>
            </div>
          </div>
        </div>
      </div>

      {isSmallScreen && (
        <div className="neo-nav-bar">
          <button className="neo-nav-menu-button" aria-controls="application-sidebar" aria-label="Toggle navigation" onClick={toggleSidebar}>
            <svg id="b" xmlns="http://www.w3.org/2000/svg" width="17.661mm" height="16.12mm" viewBox="0 0 50.062 45.694">
              <defs>
                <style></style>
              </defs>
              <g id="c">
                <line id="d" className="neo-menu-icon" x1="1" y1="1" x2="49.062" y2="1"></line>
                <line id="e" className="neo-menu-icon" x1="1" y1="22.846" x2="49.062" y2="22.846"></line>
                <line id="f" className="neo-menu-icon" x1="1" y1="44.694" x2="49.062" y2="44.694"></line>
              </g>
            </svg>
          </button>
          <div className="neo-nav-bar-logo">
            <SipLogo isAuthenticated={true} />
          </div>
        </div>
      )}
    </>
  );
};
